import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { selectActiveHighlightOpeningId } from './2D'
import { selectActiveDocumentChunkIds } from './documents'
import { selectActiveGeometryGroup } from './geometry'
import {
    FlagAPIResponse,
    FlagAxiosAPIResponse,
    FlagComments,
    FlagEnum,
    FlagFilters,
    FlagOpeningLinksAPIUpdatePayload,
    FlagOption,
    FlagOptions,
    NormalizedFlag,
    PendingFlagSwitch,
} from '../../models/flags'
import { FLAG_FILTER_TAB_OPTIONS } from '../../shared/constants/flags'
import { DEFAULT_DIALOG_HEIGHT } from '../../shared/constants/sizes'
import { getFilteredFlagsOnAPage, groupFlagsErrorsByCategory } from '../../shared/services/flags/flags-service'
import { RootState } from '../../stores'

export type MaterialFlagsState = {
    flags: NormalizedFlag[]
    activeFlag: NormalizedFlag | null
    flagOptions: FlagOptions
    isOpenFlagForm: boolean
    isTaskOrNoteInEditMode: boolean
    activeFlagComments: FlagComments[] | null
    flagTooltip: {
        visible: boolean
        flagId: number | null
    }
    flagDetails: {
        isOpen: boolean
        flagId: number | null
        openingLinkId: number | null
        position: {
            x: number
            y: number
        } | null
        dialogHeight: number
        isFixedPosition?: boolean
        paperId?: number
    }
    flagFilters: FlagFilters
    pendingFlagSwitch: PendingFlagSwitch
    shouldAdjustFlagDetailsPosition: boolean
}

export const initialMaterialFlagsState: MaterialFlagsState = {
    flags: [],
    activeFlag: null,
    flagOptions: {
        types: [],
        statuses: [],
        severities: [],
        categories: [],
        requirementSources: [],
        subCategories: [],
        intakeMethods: [],
        errors: [],
    },
    isOpenFlagForm: false,
    isTaskOrNoteInEditMode: false,
    activeFlagComments: null,
    flagTooltip: {
        visible: false,
        flagId: null,
    },
    flagDetails: {
        isOpen: false,
        flagId: null,
        openingLinkId: null,
        position: null,
        dialogHeight: DEFAULT_DIALOG_HEIGHT,
        isFixedPosition: false,
    },
    flagFilters: {
        tab: FLAG_FILTER_TAB_OPTIONS.ALL,
        status: [],
        jiraStatuses: [],
    },
    pendingFlagSwitch: null,
    shouldAdjustFlagDetailsPosition: false,
}

const getOptionData = (options: FlagOption[], id: number | null | undefined) => {
    if (!id || !options?.length) return null

    return options.find((option) => option.id === id)
}

const normalizeFlags = (flags: FlagAPIResponse[], flagOptions: FlagOptions): NormalizedFlag[] => {
    return flags.map((flag: FlagAPIResponse) => {
        const updatedFlag = {
            ...flag,
            type: getOptionData(flagOptions!.types, flag.type_id),
            status: getOptionData(flagOptions!.statuses, flag.status_id),
            severity: getOptionData(flagOptions!.severities, flag.severity_id),
            category: getOptionData(flagOptions!.categories, flag.category_id),
            requirement_source: getOptionData(flagOptions!.requirementSources, flag.requirement_source_id),
            sub_category: getOptionData(flagOptions!.subCategories, flag.sub_category_id),
            intake_method: getOptionData(flagOptions!.intakeMethods, flag.intake_method_id),
            error: getOptionData(flagOptions!.errors, flag.error_id),
        }

        // clean up unneeded fields
        delete updatedFlag.type_id
        delete updatedFlag.status_id
        delete updatedFlag.severity_id
        delete updatedFlag.category_id
        delete updatedFlag.requirement_source_id
        delete updatedFlag.sub_category_id
        delete updatedFlag.intake_method_id
        delete updatedFlag.error_id

        return updatedFlag
    })
}

// FLAGS
const handleSetFlags = (state: MaterialFlagsState, { payload }: PayloadAction<{ flags: FlagAPIResponse[] }>) => {
    state.flags = normalizeFlags(payload.flags, state.flagOptions)

    if (state.activeFlag) {
        state.activeFlag = state.flags.find((flag) => flag.id === state.activeFlag!.id) ?? null
    }
}

const handleAddNewFlag = (state: MaterialFlagsState, { payload }: PayloadAction<{ flag: FlagAPIResponse }>) => {
    const normalizedFlag = normalizeFlags([payload.flag], state.flagOptions)

    state.flags = [...state.flags, ...normalizedFlag]
}

const handleRemoveFlag = (state: MaterialFlagsState, { payload }: PayloadAction<{ flag_ids: number[] }>) => {
    state.flags = state.flags.filter((flag) => !payload.flag_ids.includes(flag.id))
}

const handleRemoveOpeningLinkFromFlag = (
    state: MaterialFlagsState,
    { payload }: PayloadAction<{ flag_id: number; opening_link_id: number }>
) => {
    const { flag_id, opening_link_id } = payload

    state.flags = state.flags.reduce<NormalizedFlag[]>((acc, flag) => {
        if (flag.id === flag_id) {
            const updatedOpeningLinks = flag.opening_links.filter((link) => link.id !== opening_link_id)

            if (updatedOpeningLinks.length > 0) {
                acc.push({
                    ...flag,
                    opening_links: updatedOpeningLinks,
                })
            }
        } else {
            acc.push(flag)
        }

        return acc
    }, [])

    if (state.activeFlag?.id === flag_id) {
        const updatedFlag = state.flags.find((flag) => flag.id === flag_id)

        state.activeFlag = updatedFlag || null
    }
}

const handleUnlinkMaterialFromFlag = (
    state: MaterialFlagsState,
    { payload }: PayloadAction<{ flag_id: number; opening_link_id: number }>
) => {
    const { flag_id, opening_link_id } = payload

    const updatedFlags = state.flags.map((flag) => {
        if (flag.id === flag_id) {
            const updatedOpeningLinks = flag.opening_links.map((ol) => ({
                ...ol,
                opening_id: ol.opening_id === opening_link_id ? null : ol.opening_id,
            }))

            return {
                ...flag,
                opening_links: updatedOpeningLinks,
            }
        }

        return flag
    })

    const activeFlagUpdate = updatedFlags.find((flag) => flag.id === flag_id)

    if (activeFlagUpdate && state.activeFlag?.id === flag_id) {
        state.activeFlag = activeFlagUpdate
    }

    state.flags = updatedFlags
}

const handleSetActiveFlag = (
    state: MaterialFlagsState,
    {
        payload,
    }: PayloadAction<{
        flag_id: number
    }>
) => {
    state.activeFlag = state.flags.find((flag) => flag.id === payload.flag_id) || null
}

const handleSetActiveFlagComments = (
    state: MaterialFlagsState,
    { payload }: PayloadAction<{ comments: FlagComments[] }>
) => {
    state.activeFlagComments = payload.comments
}

const handleSetTaskNoteEditMode = (state: MaterialFlagsState, { payload }: PayloadAction<{ isEditMode: boolean }>) => {
    state.isTaskOrNoteInEditMode = payload.isEditMode
}

const handleResetActiveFlag = (state: MaterialFlagsState) => {
    state.activeFlag = null
    state.activeFlagComments = null
}

const handleUpdateFlagStatus = (state: MaterialFlagsState, { payload }: PayloadAction<FlagAxiosAPIResponse>) => {
    const {
        id,
        status_id,
        severity_id,
        description,
        category_id,
        requirement_source_id,
        sub_category_id,
        intake_method_id,
        submitter_name,
        updated_at,
        error_id,
    } = payload.updatedFlag

    const updatedFlags = state.flags.map((flag) => {
        if (flag.id === id) {
            return {
                ...flag,
                status: getOptionData(state.flagOptions!.statuses, status_id),
                severity: getOptionData(state.flagOptions!.severities, severity_id),
                category: getOptionData(state.flagOptions!.categories, category_id),
                requirement_source: getOptionData(state.flagOptions!.requirementSources, requirement_source_id),
                sub_category: getOptionData(state.flagOptions!.subCategories, sub_category_id),
                error: getOptionData(state.flagOptions!.errors, error_id),
                intake_method: getOptionData(state.flagOptions!.intakeMethods, intake_method_id),
                description,
                submitter_name,
                updated_at,
            }
        }

        return flag
    })

    if (state.activeFlag && state.activeFlag.id === id) {
        state.activeFlag = updatedFlags.find((flag) => flag.id === id) || null
    }

    state.flags = updatedFlags
}

const handleUpdateFlagOpeningCoordinates = (
    state: MaterialFlagsState,
    { payload }: PayloadAction<{ newOpeningLinkData: FlagOpeningLinksAPIUpdatePayload[] }>
) => {
    const { newOpeningLinkData } = payload

    const updatedFlags = state.flags.map((flag) => {
        const foundNewLinkData = newOpeningLinkData.find((link) => link.flag_id === flag.id)

        if (foundNewLinkData) {
            return {
                ...flag,
                opening_links: flag.opening_links.map((link) => {
                    if (link.id === foundNewLinkData.id) {
                        return {
                            ...link,
                            coordinates: foundNewLinkData.coordinates,
                        }
                    }

                    return link
                }),
            }
        }

        return flag
    })

    state.flags = updatedFlags
}

const handleSetFlagOptions = (state: MaterialFlagsState, { payload }: PayloadAction<{ options: any }>) => {
    const sortByOrder = (arr: any[] = []) => [...arr].sort((a, b) => a.order - b.order)

    state.flagOptions = {
        types: sortByOrder(payload.options.types),
        statuses: sortByOrder(payload.options.statuses?.items),
        severities: sortByOrder(payload.options.severities?.items),
        categories: sortByOrder(payload.options.categories?.items),
        requirementSources: sortByOrder(payload.options.requirementSources?.items),
        subCategories: sortByOrder(payload.options.subCategories?.items),
        intakeMethods: sortByOrder(payload.options.intakeMethods?.items),
        errors: sortByOrder(payload.options.errors?.items),
    }
}

const handleUpdateFlag = (state: MaterialFlagsState, { payload }: PayloadAction<FlagAPIResponse[]>) => {
    const normalizedFlags = normalizeFlags(payload, state.flagOptions)

    for (const normalizedFlag of normalizedFlags) {
        if (normalizedFlag.id === state.activeFlag?.id) {
            state.activeFlag = normalizedFlag
        }

        state.flags = state.flags.map((flag) => (flag.id === normalizedFlag.id ? normalizedFlag : flag))
    }
}

const handleUpdateFlagComments = (
    state: MaterialFlagsState,
    { payload }: PayloadAction<{ flagID: number; comment: FlagComments }>
) => {
    const doesCommentExist: boolean =
        state.activeFlagComments?.some((comment) => comment.id === payload.comment.id) || false

    if (!doesCommentExist) {
        state.flags = state.flags.map((flag) => {
            if (flag.id === payload.flagID) {
                return {
                    ...flag,
                    reply_count: (flag?.reply_count ?? 0) + 1,
                }
            }

            return flag
        })
    }

    // comments do not need to be updated if the active flag is not the one being updated
    if (state.activeFlag?.id !== payload.flagID) {
        return
    }

    // create
    if (!doesCommentExist) {
        state.activeFlagComments = [...(state.activeFlagComments || []), payload.comment]
    }
    // update
    else {
        state.activeFlagComments =
            state.activeFlagComments?.map((comment) =>
                comment.id === payload.comment.id
                    ? { ...payload.comment, updated_at: new Date().toISOString() }
                    : comment
            ) || []
    }
}

const handleDeleteFlagComment = (
    state: MaterialFlagsState,
    { payload }: PayloadAction<{ flagID: number; commentID: string }>
) => {
    state.activeFlagComments = state.activeFlagComments?.filter((comment) => comment.id !== payload.commentID) || []

    state.flags = state.flags.map((flag) => {
        if (flag.id === payload.flagID) {
            return {
                ...flag,
                reply_count: (flag?.reply_count ?? 0) - 1,
            }
        }

        return flag
    })
}

function handleToggleFlagTooltip(
    state: MaterialFlagsState,
    action: PayloadAction<{ visible: boolean; flagId: number | null }>
): void {
    const { visible, flagId } = action.payload

    state.flagTooltip = {
        visible,
        flagId,
    }
}

const handleShowFlagDetails = (
    state: MaterialFlagsState,
    action: PayloadAction<Omit<MaterialFlagsState['flagDetails'], 'isOpen'>>
) => {
    state.flagDetails = {
        isOpen: true,
        ...action.payload,
        openingLinkId: action.payload.openingLinkId ?? null,
    }
}

const handleUpdateFlagDetails = (
    state: MaterialFlagsState,
    action: PayloadAction<Omit<MaterialFlagsState['flagDetails'], 'isOpen'>>
) => {
    state.flagDetails = {
        isOpen: state.flagDetails.isOpen,
        ...action.payload,
    }
}

const handleHideFlagDetails = (state: MaterialFlagsState) => {
    state.flagDetails = initialMaterialFlagsState.flagDetails
}

// ACTIONS
const handleToggleFlagForm = (state: MaterialFlagsState, { payload }: PayloadAction<{ toggleForm: boolean }>) => {
    state.isOpenFlagForm = payload.toggleForm
}

const handleSetFlagFilters = (state: MaterialFlagsState, { payload }: PayloadAction<FlagFilters>) => {
    state.flagFilters = payload
}

const handleSetFlagStatusFilters = (state: MaterialFlagsState, { payload }: PayloadAction<FlagFilters['status']>) => {
    state.flagFilters = {
        ...state.flagFilters,
        status: payload,
    }
}

const handleSetFlagJiraStatusFilters = (
    state: MaterialFlagsState,
    { payload }: PayloadAction<FlagFilters['jiraStatuses']>
) => {
    state.flagFilters = {
        ...state.flagFilters,
        jiraStatuses: payload,
    }
}

const handleResetFlagStatusFilters = (state: MaterialFlagsState) => {
    state.flagFilters = {
        ...state.flagFilters,
        status: initialMaterialFlagsState['flagFilters'].status,
        jiraStatuses: initialMaterialFlagsState['flagFilters'].jiraStatuses,
    }
}

const handleSetPendingFlagSwitch = (state: MaterialFlagsState, { payload }: PayloadAction<PendingFlagSwitch>) => {
    state.pendingFlagSwitch = payload
}

const handleClearPendingFlagSwitch = (state: MaterialFlagsState) => {
    state.pendingFlagSwitch = null
}

const handleConfirmFlagSwitch = (state: MaterialFlagsState) => {
    const pendingFlagSwitch = state.pendingFlagSwitch

    if (pendingFlagSwitch) {
        state.isTaskOrNoteInEditMode = false
        state.isOpenFlagForm = false
    }

    state.pendingFlagSwitch = null
}

const reducers = {
    setFlags: handleSetFlags,
    addNewFlag: handleAddNewFlag,
    removeFlag: handleRemoveFlag,
    removeOpeningLinkFromFlag: handleRemoveOpeningLinkFromFlag,
    setActiveFlag: handleSetActiveFlag,
    setActiveFlagComments: handleSetActiveFlagComments,
    resetActiveFlag: handleResetActiveFlag,
    updateFlagStatus: handleUpdateFlagStatus,
    updateFlagOpeningCoordinates: handleUpdateFlagOpeningCoordinates,
    unlinkFlagFromMaterial: handleUnlinkMaterialFromFlag,
    updateFlag: handleUpdateFlag,

    setOptions: handleSetFlagOptions,
    toggleFlagForm: handleToggleFlagForm,
    setTaskNoteEditMode: handleSetTaskNoteEditMode,
    updateFlagComments: handleUpdateFlagComments,
    toggleFlagTooltip: handleToggleFlagTooltip,
    showFlagDetails: handleShowFlagDetails,
    hideFlagDetails: handleHideFlagDetails,
    updateFlagDetails: handleUpdateFlagDetails,
    deleteFlagComment: handleDeleteFlagComment,
    setFlagFilters: handleSetFlagFilters,
    setFlagStatusFilters: handleSetFlagStatusFilters,
    setFlagJiraStatusFilters: handleSetFlagJiraStatusFilters,
    resetFlagStatusFilters: handleResetFlagStatusFilters,
    setPendingFlagSwitch: handleSetPendingFlagSwitch,
    clearPendingFlagSwitch: handleClearPendingFlagSwitch,
    confirmFlagSwitch: handleConfirmFlagSwitch,
}

const materialFlagsSlice = createSlice({
    name: 'materialFlags',
    initialState: initialMaterialFlagsState,
    reducers,
})

export const {
    setFlags,
    addNewFlag,
    removeFlag,
    removeOpeningLinkFromFlag,
    setActiveFlag,
    setActiveFlagComments,
    resetActiveFlag,
    updateFlagStatus,
    updateFlagOpeningCoordinates,
    unlinkFlagFromMaterial,
    updateFlag,

    setOptions,
    toggleFlagForm,
    setTaskNoteEditMode,
    updateFlagComments,
    toggleFlagTooltip,
    showFlagDetails,
    hideFlagDetails,
    updateFlagDetails,
    deleteFlagComment,
    setFlagFilters,
    setFlagStatusFilters,
    setFlagJiraStatusFilters,
    resetFlagStatusFilters,
    setPendingFlagSwitch,
    clearPendingFlagSwitch,
    confirmFlagSwitch,
} = materialFlagsSlice.actions

export default materialFlagsSlice

export const selectMaterialFlags = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flags,
    (flags) => flags
)

/**
 * Selects and filters material flags based on:
 * - Current document chunk visibility (when not showing all tabs)
 * - Selected flag type tab filter
 * - Selected status filters (while always keeping notes visible)
 * - Selected jira statuses filters (while always keeping notes visible)
 *
 * @param flags - All available material flags from state
 * @param flagFilters - Current filter settings containing tab and status
 * @param activeFloorDocIds - Currently active document chunk ids (the active floor plus any active overlays)
 * @param isShowAllTabs - Global toggle to show/hide flags across all document chunks
 *
 * @returns Filtered array of flags matching all active criteria
 */
export const selectFilteredMaterialFlags = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flags,
    (state: RootState) => state.IMUP.materialFlags.flagFilters,
    (state: RootState) => selectActiveDocumentChunkIds(state),
    (state: RootState) => state.drawable.isShowAllTabs,
    (flags, flagFilters, activeFloorDocIds, isShowAllTabs) =>
        getFilteredFlagsOnAPage(flags, isShowAllTabs, activeFloorDocIds, flagFilters)
)

export const selectErrorsGroupedByCategory = createSelector(selectFilteredMaterialFlags, (filteredFlags) => {
    const filteredFlagsWithoutNotes = filteredFlags.filter((flag) => flag?.type?.name !== FlagEnum.NOTE)

    return groupFlagsErrorsByCategory(filteredFlagsWithoutNotes)
})

/**
 * Selects statuses from flag options that are actually used in material flags
 */
export const selectStatusesUsedInMaterialFlags = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flags,
    (state: RootState) => state.IMUP.materialFlags.flagOptions.statuses,
    (flags, allStatuses) => {
        const uniqueStatusIds = new Set<number>()

        flags.forEach((flag) => {
            if (flag?.status?.id) {
                uniqueStatusIds.add(flag.status.id)
            }
        })

        return allStatuses.filter((status) => uniqueStatusIds.has(status.id))
    }
)

export const selectJiraStatusesUsedInMaterialFlags = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flags,
    (flags) => {
        const statusMap = new Map<string, { id: number; name: string }>()

        for (const { creationJiraStatus } of flags) {
            if (!creationJiraStatus) continue

            const key = `${creationJiraStatus.id}:${creationJiraStatus.jirastatusname}`

            if (!statusMap.has(key)) {
                statusMap.set(key, {
                    id: creationJiraStatus.id,
                    name: creationJiraStatus.jirastatusname,
                })
            }
        }

        return Array.from(statusMap.values())
    }
)

export const selectFlagTooltip = createSelector(
    ({ IMUP }: RootState) => IMUP.materialFlags.flagTooltip,
    (flagTooltip) => flagTooltip
)

export const selectFlagById = createSelector(
    [selectMaterialFlags, (_: RootState, flagId: number | null) => flagId],
    (flags, flagId) => flags.find((flag) => flag.id === flagId)
)

export const selectMaterialActiveFlag = createSelector(
    (state: RootState) => state.IMUP.materialFlags.activeFlag,
    (activeFlag) => activeFlag
)

export const selectActiveFlagComments = createSelector(
    (state: RootState) => state.IMUP.materialFlags.activeFlagComments,
    (activeFlagComments) => activeFlagComments
)

export const selectFlagOptions = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flagOptions,
    (flagOptions) => flagOptions
)

export const selectFlagOptionsWithoutInactive = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flagOptions,
    (flagOptions) => {
        return Object.entries(flagOptions).reduce((acc, [key, options]) => {
            acc[key] = options.filter((option) => !('is_active' in option) || option.is_active === true)

            return acc
        }, {} as typeof flagOptions)
    }
)

export const selectIsFlagFormOpen = createSelector(
    (state: RootState) => state.IMUP.materialFlags.isOpenFlagForm,
    (isOpenFlagForm) => isOpenFlagForm
)

export const selectIsTaskOrNoteInEditMode = createSelector(
    (state: RootState) => state.IMUP.materialFlags.isTaskOrNoteInEditMode,
    (isTaskOrNoteInEditMode) => isTaskOrNoteInEditMode
)

export const selectFlagFilters = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flagFilters,
    (flagFilters) => flagFilters
)

export const selectPendingFlagSwitch = createSelector(
    (state: RootState) => state.IMUP.materialFlags.pendingFlagSwitch,
    (pendingFlagSwitch) => pendingFlagSwitch
)

export const selectFlagDetails = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flagDetails,
    (flagDetails) => flagDetails
)

/**
 * The set of opening ids flags can currently be linked/unlinked/created against -- either
 * the active material's active openings, or the single active highlight's opening. Only one
 * of the two sources is ever populated at a time (selecting a highlight doesn't set an active
 * geometry group, and vice versa), so there's no need to prioritize between them.
 */
export const selectActiveFlaggableOpeningIds = createSelector(
    [selectActiveGeometryGroup, selectActiveHighlightOpeningId],
    (activeGeometryGroup, activeHighlightOpeningId): number[] => {
        if (activeHighlightOpeningId != null) {
            return [activeHighlightOpeningId]
        }

        if (!activeGeometryGroup) {
            return []
        }

        return activeGeometryGroup.openings.reduce<number[]>((ids, opening) => {
            if (opening.isActive) {
                ids.push(opening.id)
            }

            return ids
        }, [])
    }
)

export const selectNotLinkedFlagsTaskAndNotes = createSelector(
    [
        (state: RootState) => state.IMUP.materialFlags.flags, // All flags
        // activeGeometryGroup/activeHighlightOpeningId look redundant alongside
        // selectActiveFlaggableOpeningIds below (which is derived from these same two), but
        // they're not: they're needed to tell "nothing at all is selected" apart from
        // "something is selected but its openingIds happen to be empty" in the early return
        // below. Collapsing this to `openingIds.length === 0` would conflate those two cases
        // and incorrectly mark every flag as unlinked whenever a geometry group has zero
        // currently-active openings.
        selectActiveGeometryGroup,
        selectActiveHighlightOpeningId,
        selectActiveFlaggableOpeningIds,
    ],
    (allFlags, activeGeometryGroup, activeHighlightOpeningId, openingIds) => {
        const notLinkedTasks: NormalizedFlag[] = []
        const notLinkedNotes: NormalizedFlag[] = []
        const notLinkedCFBs: NormalizedFlag[] = []

        // Handle the case where there is no active geometry group and no active highlight
        if (!activeGeometryGroup && activeHighlightOpeningId == null) {
            return { notLinkedTasks, notLinkedNotes, notLinkedCFBs }
        }

        allFlags.forEach((flag) => {
            let isLinked = false

            if (openingIds.length && flag?.opening_links.length) {
                const openingLinksIds = flag.opening_links.map((link) => link.opening_id)

                // A flag is linked if ALL active openings are in the flag's opening_links
                isLinked = openingIds.every((openingId) => openingLinksIds.includes(openingId))
            }

            if (!isLinked) {
                if (flag.type?.name === FlagEnum.TASK) {
                    notLinkedTasks.push(flag)
                } else if (flag.type?.name === FlagEnum.NOTE) {
                    notLinkedNotes.push(flag)
                } else if (flag.type?.name === FlagEnum.CFB) {
                    notLinkedCFBs.push(flag)
                }
            }
        })

        return { notLinkedTasks, notLinkedNotes, notLinkedCFBs }
    }
)
// this allows knowing the index of each flag which is linked to material
export const selectMaterialFlagIndexByOpeningId = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flags,
    (flags) => {
        // Create the flagRecords by opening_id
        const flagRecords = flags.reduce<Record<string, NormalizedFlag[]>>((acc, flag) => {
            flag.opening_links.forEach(({ opening_id }) => {
                if (opening_id) {
                    acc[opening_id] = acc[opening_id] || []
                    acc[opening_id].push(flag)
                }
            })

            return acc
        }, {})

        // Transform flagRecords into Record get index by flag Id
        const result: Record<string, Record<number, number>> = Object.entries(flagRecords).reduce<
            Record<string, Record<number, number>>
        >((acc, [opening_id, flags]) => {
            acc[opening_id] = flags.reduce<Record<number, number>>((flagAcc, flag, index) => {
                flagAcc[flag.id] = index

                return flagAcc
            }, {})

            return acc
        }, {})

        return result
    }
)

export const selectFlagToDisplay = createSelector(
    (state: RootState) => state.IMUP.materialFlags.flagDetails,
    (state: RootState) => state.IMUP.materialFlags.flags,
    (flagDetails, allFlags) => {
        if (!flagDetails?.flagId || !flagDetails.openingLinkId) return null

        return (
            allFlags.find(
                (flag) =>
                    flag.id === flagDetails.flagId &&
                    flag.opening_links.some((link) => link.id === flagDetails.openingLinkId)
            ) || null
        )
    }
)
